import { useState } from 'react';

import type { NewSolicitationPayload } from '@/presentation/pages/developments/solicitations/new-solicitation-dialog.js';

export function useSolicitationActions(params: {
    createSolicitation: (params: { type: 'BUG' | 'AJUSTE_MINOR'; title: string; description: string; stepsToReproduce?: string }) => Promise<string | null>;
    uploadSolicitationEvidences: (solicitationId: string, files: File[]) => Promise<{ uploaded: number; failed: string[] }>;
    approveSolicitation: (solicitationId: string) => Promise<{ jiraWarning: string | null } | null>;
    resolveSolicitation: (solicitationId: string, notes: string) => Promise<boolean>;
    reload: () => Promise<unknown>;
    labels: { attachmentUploadFailed: (files: string) => string; resolveUploadFailed: (files: string) => string; resolveFailed: string; createFailed: string };
}) {
    const { createSolicitation, uploadSolicitationEvidences, approveSolicitation, resolveSolicitation, reload, labels } = params;
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [approvingId, setApprovingId] = useState<string | null>(null);
    const [jiraWarning, setJiraWarning] = useState<string | null>(null);
    const [resolving, setResolving] = useState(false);

    const clearError = () => setError(null);

    // Falha só nos anexos não invalida a solicitação já criada: reporta o erro mas conclui a criação.
    const attachEvidences = async (solicitationId: string, files: File[]) => {
        const { uploaded, failed } = await uploadSolicitationEvidences(solicitationId, files);
        if (failed.length > 0) {
            setError(labels.attachmentUploadFailed(failed.join(', ')));
        }
        if (uploaded > 0) {
            await reload();
        }
    };

    // Retorna `true` só quando a solicitação foi criada: o chamador usa isso para manter o diálogo aberto
    // (preservando o que o usuário digitou) quando a criação falha.
    const create = async (payload: NewSolicitationPayload): Promise<boolean> => {
        setError(null);
        setCreating(true);
        try {
            const newId = await createSolicitation({
                type: payload.type,
                title: payload.title,
                description: payload.description,
                stepsToReproduce: payload.stepsToReproduce
            });
            if (!newId) {
                setError(labels.createFailed);
                return false;
            }
            if (payload.files.length > 0) {
                // Try próprio: depois que `newId` existe a solicitação está criada, e reportar uma falha de
                // anexo como falha de criação faria o usuário reenviar o formulário e duplicar a solicitação.
                try {
                    await attachEvidences(newId, payload.files);
                } catch {
                    setError(labels.attachmentUploadFailed(payload.files.map((file) => file.name).join(', ')));
                }
            }
            return true;
        } catch {
            setError(labels.createFailed);
            return false;
        } finally {
            setCreating(false);
        }
    };

    const approve = async (solicitationId: string) => {
        setApprovingId(solicitationId);
        try {
            const approved = await approveSolicitation(solicitationId);
            setJiraWarning(approved?.jiraWarning ?? null);
        } catch {
            setError(labels.resolveFailed);
        } finally {
            setApprovingId(null);
        }
    };

    const resolve = async (targetId: string, notes: string, files: File[]): Promise<boolean> => {
        setResolving(true);
        try {
            if (files.length > 0) {
                const { failed } = await uploadSolicitationEvidences(targetId, files);
                if (failed.length > 0) {
                    setError(labels.resolveUploadFailed(failed.join(', ')));
                    return false;
                }
            }
            return await resolveSolicitation(targetId, notes);
        } catch {
            setError(labels.resolveFailed);
            return false;
        } finally {
            setResolving(false);
        }
    };

    return { creating, error, clearError, approvingId, jiraWarning, resolving, create, approve, resolve };
}
